<template>
  <div class="cutoff-settings-page">
    <div class="row">
      <div class="col-md-12">
        <div class="box box-primary">
          <div class="box-header with-border">
            <h3 class="box-title">
              <i class="fa fa-calendar-check-o"></i> Configuración de Fechas de Corte por Quincena
            </h3>
          </div>
          <div class="box-body" v-loading="loading">
            
            <!-- Panel Informativo -->
            <div class="alert alert-info" style="border-left: 5px solid #3c8dbc; margin-bottom: 25px;">
              <h4><i class="icon fa fa-info-circle"></i> Bloqueos Quincenales y Programación de Fechas de Corte</h4>
              <p>
                El sistema gestiona un <strong>registro independiente para cada quincena</strong>, permitiendo aplicar bloqueos automáticos a las 
                quincenas pasadas y programar con anticipación las fechas límites para las quincenas venideras:
              </p>
              <ul>
                <li><strong>1ª Quincena (Días 1 al 15):</strong> El límite para realizar cambios o aprobaciones vence en el día programado del <strong>mismo mes</strong> (ejemplo: quincena 1 a 15 de Septiembre con límite hasta el <strong>23 de Septiembre</strong>).</li>
                <li><strong>2ª Quincena (Días 16 al fin de mes):</strong> El límite para realizar cambios o aprobaciones vence en el día programado del <strong>mes siguiente</strong> (ejemplo: quincena 16 a 30 de Septiembre con límite hasta el <strong>6 de Octubre</strong>).</li>
                <li><strong>Quincenas Pasadas:</strong> Si la fecha límite ya pasó, la quincena queda automáticamente <strong>bloqueada</strong> para usuarios estándar.</li>
                <li><strong>Quincenas Venideras:</strong> Puedes programar o modificar con anticipación la fecha límite de cualquier quincena futura.</li>
              </ul>
            </div>

            <!-- Barra Superior de Controles -->
            <div class="top-controls-bar">
              <div class="switch-box">
                <span class="control-label">Validación General:</span>
                <el-switch
                  v-model="form.enabled"
                  active-text="Habilitada"
                  inactive-text="Deshabilitada"
                  active-color="#13ce66"
                  inactive-color="#ff4949">
                </el-switch>
              </div>

              <div class="year-filter-box">
                <span class="control-label">Filtrar por Año:</span>
                <el-select v-model="selectedYear" size="small" style="width: 120px;" @change="filterByYear">
                  <el-option v-for="y in availableYears" :key="y" :label="String(y)" :value="y"></el-option>
                  <el-option label="Todos" value="all"></el-option>
                </el-select>
              </div>

              <div class="action-buttons-box">
                <el-button 
                  type="success" 
                  size="small" 
                  icon="el-icon-plus" 
                  :disabled="!form.enabled"
                  @click="openAddDialog">
                  Nueva Quincena
                </el-button>

                <el-button 
                  type="primary" 
                  size="small" 
                  icon="el-icon-magic-stick" 
                  :disabled="!form.enabled"
                  @click="generateYearPeriodsPrompt">
                  Generar Quincenas del Año
                </el-button>

                <el-button 
                  type="warning" 
                  size="small" 
                  icon="el-icon-check" 
                  :loading="saving"
                  @click="saveConfig">
                  Guardar Cambios
                </el-button>
              </div>
            </div>

            <!-- Badges de Resumen -->
            <div class="summary-badges-container" v-if="filteredPeriods.length > 0">
              <span class="badge-item badge-total">
                <i class="fa fa-list"></i> Total: <strong>{{ filteredPeriods.length }}</strong> quincenas
              </span>
              <span class="badge-item badge-active">
                <i class="fa fa-unlock"></i> Vigentes / Abiertas: <strong>{{ countOpenPeriods }}</strong>
              </span>
              <span class="badge-item badge-blocked">
                <i class="fa fa-lock"></i> Bloqueadas / Vencidas: <strong>{{ countBlockedPeriods }}</strong>
              </span>
              <span class="badge-item badge-future">
                <i class="fa fa-calendar-o"></i> Futuras / Programadas: <strong>{{ countFuturePeriods }}</strong>
              </span>
            </div>

            <!-- Tabla de Quincenas -->
            <div class="table-responsive" style="margin-top: 15px;">
              <el-table 
                :data="filteredPeriods" 
                stripe 
                border 
                style="width: 100%"
                empty-text="No hay quincenas configuradas para el año seleccionado. Haz clic en 'Generar Quincenas del Año' o 'Nueva Quincena'.">
                
                <el-table-column label="Quincena / Período" min-width="220">
                  <template slot-scope="scope">
                    <strong style="color: #333;">{{ scope.row.name }}</strong>
                    <div style="font-size: 11px; color: #888;">ID: {{ scope.row.id }}</div>
                  </template>
                </el-table-column>

                <el-table-column label="Rango de Fechas Cubierto" width="220" align="center">
                  <template slot-scope="scope">
                    <span class="date-badge">
                      <i class="fa fa-calendar"></i> {{ scope.row.date_from }} <strong>hasta</strong> {{ scope.row.date_to }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column label="Fecha Límite de Corte (Deadline)" width="210" align="center">
                  <template slot-scope="scope">
                    <el-date-picker
                      v-model="scope.row.deadline"
                      type="date"
                      size="small"
                      format="yyyy-MM-dd"
                      value-format="yyyy-MM-dd"
                      placeholder="Seleccionar límite"
                      :disabled="!form.enabled"
                      style="width: 160px;">
                    </el-date-picker>
                  </template>
                </el-table-column>

                <el-table-column label="Estado de Aprobación" width="180" align="center">
                  <template slot-scope="scope">
                    <el-tag v-if="scope.row.closed" type="danger" size="small">
                      <i class="fa fa-lock"></i> Bloqueada Manual
                    </el-tag>
                    <el-tag v-else-if="isExpired(scope.row)" type="danger" size="small">
                      <i class="fa fa-lock"></i> Bloqueada (Vencida)
                    </el-tag>
                    <el-tag v-else-if="isOpen(scope.row)" type="success" size="small">
                      <i class="fa fa-unlock"></i> Abierta (Vigente)
                    </el-tag>
                    <el-tag v-else type="info" size="small">
                      <i class="fa fa-clock-o"></i> Futura (Programada)
                    </el-tag>
                  </template>
                </el-table-column>

                <el-table-column label="Cierre Manual" width="140" align="center">
                  <template slot-scope="scope">
                    <el-switch
                      v-model="scope.row.closed"
                      :disabled="!form.enabled"
                      active-text="Cerrar"
                      active-color="#ff4949"
                      inactive-color="#13ce66">
                    </el-switch>
                  </template>
                </el-table-column>

                <el-table-column label="Acciones" width="100" align="center" v-if="isSuperAdmin">
                  <template slot-scope="scope">
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      circle
                      :disabled="!form.enabled"
                      @click="removePeriod(scope.row)"
                      title="Eliminar esta quincena (Solo SuperAdmin)">
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- Regla por Defecto -->
            <div class="default-rules-panel" style="margin-top: 30px;">
              <h4 style="font-size: 15px; font-weight: 600; color: #555;">
                <i class="fa fa-cog text-muted"></i> Regla por Defecto para Quincenas no Listadas
              </h4>
              <p class="text-muted" style="font-size: 13px;">
                Si un registro evaluado no tiene una quincena específica registrada arriba, el sistema aplicará automáticamente estos días límites:
              </p>
              <div class="row" style="margin-top: 15px;">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Día Límite 1ª Quincena (Mismo Mes):</label>
                    <div>
                      <el-input-number 
                        v-model="form.default_q1_cutoff_day" 
                        :min="1" 
                        :max="31" 
                        :disabled="!form.enabled"
                        controls-position="right">
                      </el-input-number>
                      <span class="help-inline">Día <strong>{{ form.default_q1_cutoff_day }}</strong> del mismo mes</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Día Límite 2ª Quincena (Mes Siguiente):</label>
                    <div>
                      <el-input-number 
                        v-model="form.default_q2_cutoff_day" 
                        :min="1" 
                        :max="31" 
                        :disabled="!form.enabled"
                        controls-position="right">
                      </el-input-number>
                      <span class="help-inline">Día <strong>{{ form.default_q2_cutoff_day }}</strong> del próximo mes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Botón Inferior para Guardar -->
            <div style="margin-top: 25px; text-align: right;">
              <el-button 
                type="primary" 
                size="medium" 
                icon="el-icon-success" 
                :loading="saving"
                @click="saveConfig">
                Guardar Configuración de Fechas de Corte
              </el-button>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- DIÁLOGO PARA AGREGAR NUEVA QUINCENA MANUALMENTE -->
    <el-dialog 
      title="Agregar Quincena Personalizada" 
      :visible.sync="dialogVisible" 
      width="500px">
      <el-form :model="newPeriodForm" label-width="160px" size="small">
        <el-form-item label="Nombre de Quincena:">
          <el-input v-model="newPeriodForm.name" placeholder="Ej: Octubre 2026 - 1ª Quincena"></el-input>
        </el-form-item>
        <el-form-item label="Fecha Desde:">
          <el-date-picker 
            v-model="newPeriodForm.date_from" 
            type="date" 
            format="yyyy-MM-dd" 
            value-format="yyyy-MM-dd" 
            placeholder="Inicio quincena"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="Fecha Hasta:">
          <el-date-picker 
            v-model="newPeriodForm.date_to" 
            type="date" 
            format="yyyy-MM-dd" 
            value-format="yyyy-MM-dd" 
            placeholder="Fin quincena"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="Fecha Límite (Corte):">
          <el-date-picker 
            v-model="newPeriodForm.deadline" 
            type="date" 
            format="yyyy-MM-dd" 
            value-format="yyyy-MM-dd" 
            placeholder="Fecha límite de aprobación"
            style="width: 100%;">
          </el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">Cancelar</el-button>
        <el-button size="small" type="primary" @click="addCustomPeriod">Agregar Quincena</el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
export default {
  name: 'CutoffSettings',
  data() {
    return {
      loading: false,
      saving: false,
      selectedYear: new Date().getFullYear(),
      form: {
        enabled: false,
        default_q1_cutoff_day: 23,
        default_q2_cutoff_day: 6,
        periods: [],
      },
      dialogVisible: false,
      newPeriodForm: {
        name: '',
        date_from: '',
        date_to: '',
        deadline: '',
      }
    };
  },
  computed: {
    availableYears() {
      let years = new Set();
      const currentYear = new Date().getFullYear();
      years.add(currentYear - 1);
      years.add(currentYear);
      years.add(currentYear + 1);

      if (this.form.periods && this.form.periods.length > 0) {
        this.form.periods.forEach(p => {
          if (p.year) years.add(p.year);
          else if (p.date_from) {
            let y = parseInt(p.date_from.substring(0, 4));
            if (!isNaN(y)) years.add(y);
          }
        });
      }
      return Array.from(years).sort((a, b) => b - a);
    },
    filteredPeriods() {
      if (!this.form.periods) return [];
      if (this.selectedYear === 'all') return this.form.periods;

      return this.form.periods.filter(p => {
        if (p.year) return p.year === this.selectedYear;
        if (p.date_from) return parseInt(p.date_from.substring(0, 4)) === this.selectedYear;
        return true;
      });
    },
    countOpenPeriods() {
      return this.filteredPeriods.filter(p => this.isOpen(p)).length;
    },
    countBlockedPeriods() {
      return this.filteredPeriods.filter(p => this.isExpired(p) || p.closed).length;
    },
    countFuturePeriods() {
      return this.filteredPeriods.filter(p => this.isFuture(p)).length;
    },
    isSuperAdmin() {
      if (!this.$auth || !this.$auth.user) return false;
      const user = this.$auth.user;
      const userProf = typeof user.profile === 'object' && user.profile !== null
        ? user.profile.profile
        : user.profile;
      const userEmail = user.email || (typeof user.profile === 'object' && user.profile !== null ? user.profile.email : '') || '';
      return ['SADM', 'SuperAdmin', 'SuperAdministrador', 'SUPER_ADM'].includes(userProf)
        || userEmail === 'superadminsgp@labortime.com'
        || (typeof userEmail === 'string' && userEmail.startsWith('soporte@'));
    }
  },
  mounted() {
    this.fetchConfig();
  },
  methods: {
    fetchConfig() {
      this.loading = true;
      axios.get('/api/config/cutoff-dates')
        .then(response => {
          if (response.data && response.data.data) {
            let data = response.data.data;
            this.form = {
              enabled: Boolean(data.enabled),
              default_q1_cutoff_day: data.default_q1_cutoff_day ? parseInt(data.default_q1_cutoff_day) : 23,
              default_q2_cutoff_day: data.default_q2_cutoff_day ? parseInt(data.default_q2_cutoff_day) : 6,
              periods: Array.isArray(data.periods) ? data.periods : [],
            };
          }
        })
        .catch(error => {
          this.$message.error('No se pudo cargar la configuración de fechas de corte.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveConfig() {
      this.saving = true;
      axios.post('/api/config/cutoff-dates', this.form)
        .then(response => {
          this.$message({
            message: response.data.message || 'Configuración de fechas de corte guardada exitosamente.',
            type: 'success'
          });
        })
        .catch(error => {
          let msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al guardar la configuración.';
          this.$message.error(msg);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    filterByYear(val) {
      this.selectedYear = val;
    },
    generateYearPeriodsPrompt() {
      let targetYear = this.selectedYear === 'all' ? new Date().getFullYear() : this.selectedYear;
      this.$confirm(`¿Deseas generar/actualizar las 24 quincenas para el año ${targetYear} con las fechas de corte estándar? (1ª Q: día ${this.form.default_q1_cutoff_day} del mismo mes, 2ª Q: día ${this.form.default_q2_cutoff_day} del próximo mes)`, 'Confirmar Generación', {
        confirmButtonText: 'Generar Quincenas',
        cancelButtonText: 'Cancelar',
        type: 'info'
      }).then(() => {
        this.generateYearPeriods(targetYear);
      }).catch(() => {});
    },
    generateYearPeriods(year) {
      this.loading = true;
      axios.post('/api/config/cutoff-dates/generate', {
        year: year,
        default_q1_cutoff_day: this.form.default_q1_cutoff_day,
        default_q2_cutoff_day: this.form.default_q2_cutoff_day,
      })
      .then(response => {
        if (response.data && response.data.periods) {
          let newPeriods = response.data.periods;
          // Merge: remove any existing periods for that year and append new ones
          let filtered = this.form.periods.filter(p => {
            let y = p.year ? p.year : (p.date_from ? parseInt(p.date_from.substring(0, 4)) : 0);
            return y !== year;
          });
          this.form.periods = [...filtered, ...newPeriods].sort((a, b) => a.date_from.localeCompare(b.date_from));
          this.$message.success(`Se generaron exitosamente las 24 quincenas para el año ${year}. Recuerda hacer clic en 'Guardar Cambios'.`);
        }
      })
      .catch(error => {
        this.$message.error('Error al generar las quincenas del año.');
      })
      .finally(() => {
        this.loading = false;
      });
    },
    openAddDialog() {
      this.newPeriodForm = {
        name: '',
        date_from: '',
        date_to: '',
        deadline: '',
      };
      this.dialogVisible = true;
    },
    addCustomPeriod() {
      if (!this.newPeriodForm.name || !this.newPeriodForm.date_from || !this.newPeriodForm.date_to || !this.newPeriodForm.deadline) {
        this.$message.warning('Por favor completa todos los campos de la quincena.');
        return;
      }
      let year = parseInt(this.newPeriodForm.date_from.substring(0, 4));
      let id = `custom-${this.newPeriodForm.date_from}`;

      this.form.periods.push({
        id: id,
        year: year,
        name: this.newPeriodForm.name,
        date_from: this.newPeriodForm.date_from,
        date_to: this.newPeriodForm.date_to,
        deadline: this.newPeriodForm.deadline,
        closed: false,
      });

      this.form.periods.sort((a, b) => a.date_from.localeCompare(b.date_from));
      this.dialogVisible = false;
      this.$message.success('Quincena agregada a la lista. No olvides guardar los cambios.');
    },
    removePeriod(row) {
      if (!this.isSuperAdmin) {
        this.$message.error('Solo los usuarios SuperAdministradores pueden eliminar quincenas.');
        return;
      }
      const periodName = row && row.name ? `"${row.name}"` : 'esta quincena';
      this.$confirm(`¿Estás seguro de eliminar ${periodName} de la lista de configuración? Esta acción solo debe realizarse por contingencia y está reservada para SuperAdministradores.`, 'Confirmar Eliminación (Solo SuperAdmin)', {
        confirmButtonText: 'Sí, Eliminar',
        cancelButtonText: 'Cancelar',
        type: 'warning'
      }).then(() => {
        this.form.periods = this.form.periods.filter(p => {
          if (row.id && p.id) return p.id !== row.id;
          return !(p.date_from === row.date_from && p.date_to === row.date_to);
        });
        this.$message.info('Quincena removida de la lista. Haz clic en "Guardar Configuración" para persistir el cambio.');
      }).catch(() => {});
    },
    getTodayDateString() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    isExpired(row) {
      if (row.closed) return true;
      if (!row.deadline) return false;
      return this.getTodayDateString() > row.deadline;
    },
    isOpen(row) {
      if (row.closed) return false;
      if (!row.deadline) return true;
      const today = this.getTodayDateString();
      return today <= row.deadline && today >= row.date_from;
    },
    isFuture(row) {
      if (row.closed) return false;
      return this.getTodayDateString() < row.date_from;
    }
  }
};
</script>

<style scoped>
.cutoff-settings-page {
  padding: 15px;
}
.top-controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f9fafc;
  padding: 15px 20px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  flex-wrap: wrap;
  gap: 15px;
}
.control-label {
  font-weight: 600;
  color: #555;
  margin-right: 10px;
  font-size: 13px;
}
.switch-box, .year-filter-box, .action-buttons-box {
  display: flex;
  align-items: center;
  gap: 8px;
}
.summary-badges-container {
  display: flex;
  gap: 12px;
  margin-top: 15px;
  flex-wrap: wrap;
}
.badge-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
}
.badge-total {
  background-color: #f4f4f5;
  color: #606266;
  border: 1px solid #dcdfe6;
}
.badge-active {
  background-color: #f0f9eb;
  color: #67c23a;
  border: 1px solid #c2e7b0;
}
.badge-blocked {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
}
.badge-future {
  background-color: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
}
.date-badge {
  font-family: monospace;
  font-size: 12px;
  color: #444;
  background: #f8f9fa;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}
.default-rules-panel {
  background: #fbfbfb;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 18px 20px;
}
.help-inline {
  color: #737373;
  font-size: 13px;
  margin-left: 12px;
}
</style>
